import {loaded} from '@github-ui/document-ready'
import {runAfterPendingGlobalNavigationRefresh} from '@github-ui/global-navigation/refresh-global-navigation'
import {sendPageView} from '@github-ui/hydro-analytics'
import {SOFT_NAV_STATE} from '@github-ui/soft-nav/states'
import {getSoftNavMechanism} from '@github-ui/soft-nav/utils'
import {ssrSafeDocument} from '@github-ui/ssr-utils'
import {getCurrentReactAppName} from '@github-ui/stats-metadata'
import type {Context} from '@github/hydro-analytics-client'

const initialization = Symbol.for('github.analytics-behaviors.pageviews')
type AnalyticsDocument = Document & {[initialization]?: () => void}

function getContext(context: Context = {}) {
  const reactAppName = getCurrentReactAppName()
  if (!reactAppName) return context
  return {...context, react_app: reactAppName}
}

export function setupAnalyticsPageviews(): (() => void) | undefined {
  const document: AnalyticsDocument | undefined = ssrSafeDocument
  if (!document) return
  if (document[initialization]) return document[initialization]

  let active = true
  const sendFramePageView = () => sendPageView(getContext({turbo: 'true'}))
  const sendSoftNavigationPageView = () => {
    if (getSoftNavMechanism() === 'turbo.frame') return
    void runAfterPendingGlobalNavigationRefresh(() => {
      if (active) sendPageView(getContext({turbo: 'true'}))
    })
  }

  // Turbo loads should be treated like pageloads.
  document.addEventListener(SOFT_NAV_STATE.FRAME_UPDATE, sendFramePageView)
  document.addEventListener(SOFT_NAV_STATE.SUCCESS, sendSoftNavigationPageView)

  // Send a page view as soon as the page is loaded.
  void (async () => {
    await loaded
    if (active) sendPageView(getContext())
  })()

  const cleanup = () => {
    active = false
    document.removeEventListener(SOFT_NAV_STATE.FRAME_UPDATE, sendFramePageView)
    document.removeEventListener(SOFT_NAV_STATE.SUCCESS, sendSoftNavigationPageView)
    delete document[initialization]
  }
  document[initialization] = cleanup
  return cleanup
}
